import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Users, UserPlus, Search, Filter, MoreVertical,
  Phone, Mail, Building2, Tag, Calendar, CheckCircle,
  Clock, AlertCircle, ArrowRight, FileText, Home,
  TrendingUp, DollarSign, X, Plus, ChevronDown,
  Edit, Trash2, Eye, MessageSquare, LayoutGrid, List,
  Download, RefreshCw, LogOut
} from 'lucide-react';

import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL.replace('/api', '');

const CRMPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login, logout, authAxios } = useAuth();
  
  // Login State
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [contacts, setContacts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [viewMode, setViewMode] = useState('pipeline'); // pipeline or list
  const [filterStage, setFilterStage] = useState('');
  const [draggedContact, setDraggedContact] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // New contact form
  const [newContact, setNewContact] = useState({
    name: '', email: '', phone: '', company: '',
    service_interest: '', source: 'Website Form', estimated_value: 0
  });

  // New task form
  const [newTask, setNewTask] = useState({
    title: '', description: '', due_date: '', priority: 'medium', contact_id: ''
  });

  // New activity form
  const [newActivity, setNewActivity] = useState({
    type: 'note', title: '', description: '', contact_id: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchConfig();
      fetchDashboard();
      fetchContacts();
      fetchTasks();
    }
  }, [isAuthenticated]);

  const fetchConfig = async () => {
    try {
      const response = await authAxios.get('/crm/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch CRM config:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await authAxios.get('/crm/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterStage) params.stage = filterStage;
      
      const response = await authAxios.get(`/crm/contacts`, { params });
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await authAxios.get(`/crm/tasks`);
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchContactDetails = async (contactId) => {
    try {
      const response = await authAxios.get(`/crm/contacts/${contactId}`);
      setSelectedContact(response.data);
    } catch (error) {
      console.error('Failed to fetch contact details:', error);
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name) {
      toast.error('Name is required');
      return;
    }
    try {
      await authAxios.post(`/crm/contacts`, newContact);
      toast.success('Contact created successfully!');
      setShowContactModal(false);
      setNewContact({
        name: '', email: '', phone: '', company: '',
        service_interest: '', source: 'Website Form', estimated_value: 0
      });
      fetchContacts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const handleUpdateStage = async (contactId, newStage) => {
    try {
      await authAxios.patch(`/crm/contacts/${contactId}/stage?stage=${newStage}`);
      fetchContacts();
      fetchDashboard();
      if (selectedContact?.contact?.id === contactId) {
        fetchContactDetails(contactId);
      }
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.due_date) {
      toast.error('Title and due date are required');
      return;
    }
    try {
      await authAxios.post(`/crm/tasks`, newTask);
      toast.success('Task created successfully!');
      setShowTaskModal(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', contact_id: '' });
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await authAxios.put(`/crm/tasks/${taskId}`, { status: 'completed' });
      toast.success('Task completed!');
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleCreateActivity = async () => {
    if (!newActivity.title || !newActivity.contact_id) {
      toast.error('Title and contact are required');
      return;
    }
    try {
      await authAxios.post(`/crm/activities`, newActivity);
      toast.success('Activity logged!');
      setShowActivityModal(false);
      setNewActivity({ type: 'note', title: '', description: '', contact_id: '' });
      if (selectedContact?.contact?.id === newActivity.contact_id) {
        fetchContactDetails(newActivity.contact_id);
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to log activity');
    }
  };

  const handleImportSubmissions = async () => {
    try {
      const response = await authAxios.post(`/crm/import-submissions`);
      toast.success(response.data.message);
      fetchContacts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to import submissions');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await authAxios.delete(`/crm/contacts/${contactId}`);
      toast.success('Contact deleted');
      setSelectedContact(null);
      fetchContacts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStage) params.append('stage', filterStage);
      
      const response = await authAxios.get(`/crm/contacts/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aureate_crm_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Leads exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export leads');
    }
  };

  const getStageColor = (stageId) => {
    const stage = config?.pipeline_stages?.find(s => s.id === stageId);
    return stage?.color || '#6B7280';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP', minimumFractionDigits: 0
    }).format(value || 0);
  };

  // Tabs
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'tasks', label: 'Tasks', icon: CheckCircle },
  ];

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setPinError(false);
    
    const result = await login(pinInput);
    
    if (!result.success) {
      setPinError(true);
      setPinInput('');
      setTimeout(() => setPinError(false), 2000);
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
          <p className="text-white/50 mt-4 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Show PIN entry screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#111111] border border-white/10 p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_aureate-preview/artifacts/h6n4fhlc_Only%20Logo.png"
              alt="Aureate Residences"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="font-mono text-xl font-bold">CRM Access</h1>
            <p className="text-white/50 text-sm mt-2">Enter PIN to continue</p>
          </div>
          
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter PIN"
                maxLength={4}
                autoFocus
                disabled={loginLoading}
                data-testid="crm-pin-input"
                className={`w-full bg-[#0A0A0A] border ${
                  pinError ? 'border-red-500' : 'border-white/10'
                } px-4 py-4 font-mono text-center text-2xl tracking-[0.5em] focus:border-[#D4AF37] outline-none transition-colors disabled:opacity-50`}
              />
              {pinError && (
                <p className="text-red-400 text-sm text-center mt-2">Invalid PIN</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loginLoading || pinInput.length !== 4}
              data-testid="crm-login-button"
              className="w-full bg-[#D4AF37] text-black py-3 font-mono font-bold hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Access CRM'
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center space-y-2">
            <Link to="/admin" className="text-[#D4AF37] text-sm hover:underline block">
              Go to Admin Panel
            </Link>
            <Link to="/" className="text-white/50 text-sm hover:text-white transition-colors block">
              ← Back to Website
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="bg-[#111111] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-mono text-2xl font-bold tracking-tight">CRM</h1>
            <p className="text-white/50 text-sm mt-1">Manage leads, contacts & pipeline</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={handleImportSubmissions}
              className="px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors"
            >
              Import Form Submissions
            </button>
            <button
              onClick={() => setShowContactModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add Contact
            </button>
            <Link
              to="/admin"
              className="px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors"
            >
              Admin Panel
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 border border-red-500/30 text-red-400/70 font-mono text-sm hover:bg-red-500/10 transition-colors"
              data-testid="crm-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-[#111111] border border-white/5 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-xs font-mono uppercase">Total Contacts</p>
                    <p className="text-3xl font-bold mt-2">{dashboard.total_contacts}</p>
                  </div>
                  <Users className="w-8 h-8 text-[#D4AF37]/30" />
                </div>
                <p className="text-white/50 text-xs mt-4">+{dashboard.new_this_month} this month</p>
              </div>
              
              <div className="bg-[#111111] border border-white/5 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-xs font-mono uppercase">Pipeline Value</p>
                    <p className="text-3xl font-bold mt-2">{formatCurrency(dashboard.total_pipeline_value)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-[#D4AF37]/30" />
                </div>
              </div>
              
              <div className="bg-[#111111] border border-white/5 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-xs font-mono uppercase">Conversion Rate</p>
                    <p className="text-3xl font-bold mt-2">{dashboard.conversion_rate}%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500/30" />
                </div>
                <p className="text-white/50 text-xs mt-4">{dashboard.won_count} won / {dashboard.lost_count} lost</p>
              </div>
              
              <div className="bg-[#111111] border border-white/5 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-xs font-mono uppercase">Pending Tasks</p>
                    <p className="text-3xl font-bold mt-2">{dashboard.pending_tasks}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-blue-500/30" />
                </div>
                {dashboard.overdue_tasks > 0 && (
                  <p className="text-red-400 text-xs mt-4">{dashboard.overdue_tasks} overdue</p>
                )}
              </div>
            </div>

            {/* Pipeline Overview */}
            <div className="bg-[#111111] border border-white/5 p-6">
              <h3 className="font-mono font-bold mb-4">Pipeline Overview</h3>
              <div className="grid grid-cols-7 gap-2">
                {dashboard.pipeline_stats?.map(stage => (
                  <div key={stage.id} className="text-center p-3 bg-[#0A0A0A] border border-white/5">
                    <div 
                      className="w-3 h-3 rounded-full mx-auto mb-2"
                      style={{ backgroundColor: stage.color }}
                    />
                    <p className="text-xs text-white/50">{stage.name}</p>
                    <p className="text-xl font-bold mt-1">{stage.count}</p>
                    <p className="text-xs text-white/30">{formatCurrency(stage.value)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity & Tasks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <div className="bg-[#111111] border border-white/5 p-6">
                <h3 className="font-mono font-bold mb-4">Recent Activity</h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {dashboard.recent_activities?.map(activity => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 bg-[#0A0A0A] border border-white/5">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{activity.title}</p>
                        <p className="text-xs text-white/50">{activity.contact_name}</p>
                        <p className="text-xs text-white/30 mt-1">{formatDate(activity.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Source Distribution */}
              <div className="bg-[#111111] border border-white/5 p-6">
                <h3 className="font-mono font-bold mb-4">Lead Sources</h3>
                <div className="space-y-3">
                  {dashboard.source_stats?.map(source => (
                    <div key={source.source} className="flex items-center justify-between">
                      <span className="text-sm text-white/70">{source.source}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-[#0A0A0A] rounded overflow-hidden">
                          <div 
                            className="h-full bg-[#D4AF37]"
                            style={{ width: `${(source.count / dashboard.total_contacts) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-mono">{source.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setTimeout(fetchContacts, 300);
                    }}
                    className="w-full bg-[#111111] border border-white/10 pl-10 pr-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <select
                  value={filterStage}
                  onChange={(e) => {
                    setFilterStage(e.target.value);
                    setTimeout(fetchContacts, 100);
                  }}
                  className="bg-[#111111] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                >
                  <option value="">All Stages</option>
                  {config?.pipeline_stages?.map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('pipeline')}
                  className={`p-2 ${viewMode === 'pipeline' ? 'text-[#D4AF37]' : 'text-white/50'}`}
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'text-[#D4AF37]' : 'text-white/50'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Pipeline View */}
            {viewMode === 'pipeline' && (
              <div className="grid grid-cols-7 gap-3 overflow-x-auto pb-4" data-testid="pipeline-view">
                {config?.pipeline_stages?.map(stage => {
                  const stageContacts = contacts.filter(c => c.stage === stage.id);
                  const isOver = dragOverStage === stage.id;
                  return (
                    <div
                      key={stage.id}
                      className="min-w-[200px]"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverStage(stage.id);
                      }}
                      onDragLeave={() => setDragOverStage(null)}
                      onDrop={async (e) => {
                        e.preventDefault();
                        setDragOverStage(null);
                        const contactId = e.dataTransfer.getData('text/plain');
                        if (contactId && draggedContact && draggedContact.stage !== stage.id) {
                          const stageName = stage.name;
                          toast.success(`Moved to ${stageName}`);
                          await handleUpdateStage(contactId, stage.id);
                        }
                        setDraggedContact(null);
                      }}
                      data-testid={`pipeline-column-${stage.id}`}
                    >
                      <div 
                        className="flex items-center gap-2 p-3 mb-2"
                        style={{ borderLeft: `3px solid ${stage.color}` }}
                      >
                        <span className="text-sm font-mono font-bold">{stage.name}</span>
                        <span className="text-xs text-white/30 ml-auto">{stageContacts.length}</span>
                      </div>
                      <div className={`space-y-2 max-h-[60vh] overflow-y-auto min-h-[80px] rounded transition-all ${
                        isOver ? 'bg-[#D4AF37]/5 border-2 border-dashed border-[#D4AF37]/40' : 'border-2 border-transparent'
                      }`}>
                        {stageContacts.map(contact => (
                          <div
                            key={contact.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', contact.id);
                              e.dataTransfer.effectAllowed = 'move';
                              setDraggedContact(contact);
                            }}
                            onDragEnd={() => {
                              setDraggedContact(null);
                              setDragOverStage(null);
                            }}
                            onClick={() => fetchContactDetails(contact.id)}
                            className={`bg-[#111111] border border-white/5 p-3 cursor-grab active:cursor-grabbing hover:border-[#D4AF37]/30 transition-all select-none ${
                              draggedContact?.id === contact.id ? 'opacity-40 scale-95' : ''
                            }`}
                            data-testid={`pipeline-card-${contact.id}`}
                          >
                            <p className="font-medium text-sm truncate">{contact.name}</p>
                            {contact.company && (
                              <p className="text-xs text-white/50 truncate">{contact.company}</p>
                            )}
                            {contact.service_interest && (
                              <span className="inline-block mt-2 px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs">
                                {contact.service_interest}
                              </span>
                            )}
                            {contact.estimated_value > 0 && (
                              <p className="text-xs text-white/30 mt-2">{formatCurrency(contact.estimated_value)}</p>
                            )}
                          </div>
                        ))}
                        {stageContacts.length === 0 && !isOver && (
                          <div className="text-center py-6 text-white/15 text-xs font-mono">
                            Drop here
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="bg-[#111111] border border-white/5">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Name</th>
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Contact</th>
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Service</th>
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Stage</th>
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Value</th>
                      <th className="text-left p-4 font-mono text-xs text-white/50 uppercase">Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(contact => (
                      <tr 
                        key={contact.id}
                        onClick={() => fetchContactDetails(contact.id)}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      >
                        <td className="p-4">
                          <p className="font-medium">{contact.name}</p>
                          {contact.company && <p className="text-xs text-white/50">{contact.company}</p>}
                        </td>
                        <td className="p-4">
                          <p className="text-sm">{contact.email}</p>
                          <p className="text-xs text-white/50">{contact.phone}</p>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs">
                            {contact.service_interest || '-'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span 
                            className="px-2 py-1 text-xs text-white"
                            style={{ backgroundColor: getStageColor(contact.stage) }}
                          >
                            {config?.pipeline_stages?.find(s => s.id === contact.stage)?.name}
                          </span>
                        </td>
                        <td className="p-4 font-mono text-sm">{formatCurrency(contact.estimated_value)}</td>
                        <td className="p-4 text-sm text-white/50">{formatDate(contact.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setShowTaskModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map(task => (
                <div 
                  key={task.id}
                  className={`bg-[#111111] border p-4 ${
                    task.status === 'completed' ? 'border-green-500/30 opacity-60' : 'border-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>
                        {task.title}
                      </p>
                      {task.contact_name && (
                        <p className="text-xs text-white/50 mt-1">{task.contact_name}</p>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 text-xs ${
                      task.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                      task.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {task.priority}
                    </span>
                  </div>
                  {task.description && (
                    <p className="text-sm text-white/50 mt-2 line-clamp-2">{task.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                    <span className={`text-xs flex items-center gap-1 ${
                      new Date(task.due_date) < new Date() && task.status !== 'completed'
                        ? 'text-red-400' : 'text-white/50'
                    }`}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(task.due_date)}
                    </span>
                    {task.status !== 'completed' && (
                      <button
                        onClick={() => handleCompleteTask(task.id)}
                        className="text-xs text-green-400 hover:text-green-300"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Contact Detail Sidebar */}
      <AnimatePresence>
        {selectedContact && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="fixed top-0 right-0 w-full max-w-lg h-full bg-[#111111] border-l border-white/10 z-50 overflow-y-auto"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold">{selectedContact.contact?.name}</h2>
                  {selectedContact.contact?.company && (
                    <p className="text-white/50">{selectedContact.contact?.company}</p>
                  )}
                </div>
                <button onClick={() => setSelectedContact(null)} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                {selectedContact.contact?.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-white/30" />
                    <a href={`mailto:${selectedContact.contact?.email}`} className="text-[#D4AF37] hover:underline">
                      {selectedContact.contact?.email}
                    </a>
                  </div>
                )}
                {selectedContact.contact?.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-white/30" />
                    <a href={`tel:${selectedContact.contact?.phone}`} className="hover:text-[#D4AF37]">
                      {selectedContact.contact?.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Stage Selector */}
              <div className="mb-6">
                <label className="text-xs text-white/50 font-mono uppercase block mb-2">Stage</label>
                <select
                  value={selectedContact.contact?.stage}
                  onChange={(e) => handleUpdateStage(selectedContact.contact?.id, e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm"
                >
                  {config?.pipeline_stages?.map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => {
                    setNewActivity({ ...newActivity, contact_id: selectedContact.contact?.id });
                    setShowActivityModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm hover:border-[#D4AF37]/30"
                >
                  <MessageSquare className="w-4 h-4" />
                  Log Activity
                </button>
                <button
                  onClick={() => {
                    setNewTask({ ...newTask, contact_id: selectedContact.contact?.id });
                    setShowTaskModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm hover:border-[#D4AF37]/30"
                >
                  <CheckCircle className="w-4 h-4" />
                  Add Task
                </button>
              </div>

              {/* Original Message */}
              {selectedContact.contact?.original_message && (
                <div className="mb-6 p-4 bg-[#0A0A0A] border border-white/5">
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Original Message</label>
                  <p className="text-sm text-white/70">{selectedContact.contact?.original_message}</p>
                </div>
              )}

              {/* Activity Timeline */}
              <div className="mb-6">
                <h3 className="font-mono font-bold text-sm mb-4">Activity Timeline</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {selectedContact.activities?.map(activity => (
                    <div key={activity.id} className="flex gap-3 p-3 bg-[#0A0A0A] border border-white/5">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        {activity.type === 'stage_change' ? (
                          <ArrowRight className="w-4 h-4 text-[#D4AF37]" />
                        ) : activity.type === 'call' ? (
                          <Phone className="w-4 h-4 text-[#D4AF37]" />
                        ) : activity.type === 'email' ? (
                          <Mail className="w-4 h-4 text-[#D4AF37]" />
                        ) : (
                          <FileText className="w-4 h-4 text-[#D4AF37]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{activity.title}</p>
                        {activity.description && (
                          <p className="text-xs text-white/50 mt-1">{activity.description}</p>
                        )}
                        <p className="text-xs text-white/30 mt-2">{formatDate(activity.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tasks */}
              {selectedContact.tasks?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-mono font-bold text-sm mb-4">Tasks</h3>
                  <div className="space-y-2">
                    {selectedContact.tasks?.map(task => (
                      <div key={task.id} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/5">
                        <button
                          onClick={() => handleCompleteTask(task.id)}
                          className={`w-5 h-5 border rounded ${
                            task.status === 'completed' 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-white/30 hover:border-[#D4AF37]'
                          }`}
                        >
                          {task.status === 'completed' && <CheckCircle className="w-4 h-4 text-white" />}
                        </button>
                        <div className="flex-1">
                          <p className={`text-sm ${task.status === 'completed' ? 'line-through text-white/50' : ''}`}>
                            {task.title}
                          </p>
                          <p className="text-xs text-white/30">{formatDate(task.due_date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Button */}
              <button
                onClick={() => handleDeleteContact(selectedContact.contact?.id)}
                className="w-full py-3 border border-red-500/30 text-red-400 font-mono text-sm hover:bg-red-500/10 transition-colors"
              >
                Delete Contact
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#111111] border border-white/10 w-full max-w-lg p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-mono font-bold text-lg mb-6">Add New Contact</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={e => setNewContact({...newContact, name: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Company</label>
                    <input
                      type="text"
                      value={newContact.company}
                      onChange={e => setNewContact({...newContact, company: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Email</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={e => setNewContact({...newContact, email: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Phone</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={e => setNewContact({...newContact, phone: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Service Interest</label>
                    <select
                      value={newContact.service_interest}
                      onChange={e => setNewContact({...newContact, service_interest: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    >
                      <option value="">Select...</option>
                      <option value="ACCOMMODATION">Accommodation Solutions</option>
                      <option value="HMO">HMO & Shared Living</option>
                      <option value="LANDLORD">Property & Landlord Solutions</option>
                      <option value="PROPERTY_REQ">Property Requirement</option>
                      <option value="LANDLORD_SOL">Landlord Solution</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Source</label>
                    <select
                      value={newContact.source}
                      onChange={e => setNewContact({...newContact, source: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    >
                      {config?.contact_sources?.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Estimated Value (£)</label>
                  <input
                    type="number"
                    value={newContact.estimated_value}
                    onChange={e => setNewContact({...newContact, estimated_value: parseFloat(e.target.value) || 0})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowContactModal(false)}
                    className="flex-1 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateContact}
                    className="flex-1 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90"
                  >
                    Create Contact
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Task Modal */}
      <AnimatePresence>
        {showTaskModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTaskModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#111111] border border-white/10 w-full max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-mono font-bold text-lg mb-6">Add New Task</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Title *</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({...newTask, title: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Description</label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({...newTask, description: e.target.value})}
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Due Date *</label>
                    <input
                      type="date"
                      value={newTask.due_date}
                      onChange={e => setNewTask({...newTask, due_date: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 font-mono uppercase block mb-2">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({...newTask, priority: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowTaskModal(false)}
                    className="flex-1 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTask}
                    className="flex-1 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90"
                  >
                    Create Task
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Activity Modal */}
      <AnimatePresence>
        {showActivityModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowActivityModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#111111] border border-white/10 w-full max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-mono font-bold text-lg mb-6">Log Activity</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Activity Type</label>
                  <select
                    value={newActivity.type}
                    onChange={e => setNewActivity({...newActivity, type: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                  >
                    {config?.activity_types?.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Title *</label>
                  <input
                    type="text"
                    value={newActivity.title}
                    onChange={e => setNewActivity({...newActivity, title: e.target.value})}
                    placeholder="e.g., Follow-up call scheduled"
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 font-mono uppercase block mb-2">Notes</label>
                  <textarea
                    value={newActivity.description}
                    onChange={e => setNewActivity({...newActivity, description: e.target.value})}
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowActivityModal(false)}
                    className="flex-1 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateActivity}
                    className="flex-1 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90"
                  >
                    Log Activity
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CRMPage;
